import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, X, Plus } from "lucide-react";

const DISMISS_KEY = "dr_install_dismissed";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 3; // 3 days

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const t = parseInt(raw, 10);
  return Number.isFinite(t) && Date.now() - t < DISMISS_TTL_MS;
}

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari has no beforeinstallprompt event
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => {
        setIosMode(true);
        setVisible(true);
      }, 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredEvent) return;
    deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferredEvent(null);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="fixed inset-x-0 bottom-4 z-40 mx-auto max-w-md px-4"
          data-testid="install-prompt"
        >
          <div className="dr-glass flex items-start gap-3 rounded-2xl border p-4">
            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#E1FF00] text-black">
              <Download size={18} />
            </div>
            <div className="flex-1">
              <div className="font-display text-base text-white">
                Install DailyRacer
              </div>
              {iosMode ? (
                <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                  Tap <Share size={11} className="inline align-[-2px]" /> then
                  <span className="mx-1 rounded bg-white/10 px-1.5 py-0.5 font-semibold text-zinc-200">
                    <Plus size={10} className="inline align-[-1px]" /> Add to Home Screen
                  </span>
                  so today's tip alert can reach you.
                </p>
              ) : (
                <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                  Add to your home screen for one-tap access and daily push alerts.
                </p>
              )}
              {!iosMode && (
                <button
                  onClick={install}
                  data-testid="install-prompt-install-btn"
                  className="dr-btn-primary mt-3 rounded-full px-4 py-1.5 text-xs"
                >
                  Install
                </button>
              )}
            </div>
            <button
              onClick={dismiss}
              data-testid="install-prompt-dismiss-btn"
              className="rounded-full border border-zinc-800 p-1.5 text-zinc-500 hover:text-white"
            >
              <X size={12} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
